import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API = '/api';

const fmt = (n) => (n || 0).toLocaleString();
const fmtMMK = (n) => `${fmt(n)} MMK`;
const pct = (a, b) => b ? ((a / b) * 100).toFixed(1) : '0.0';
const trendIcon = (cur, prev) => {
  if (cur > prev) return '▲';
  if (cur < prev) return '▼';
  return '—';
};
const trendColor = (cur, prev) => {
  if (cur > prev) return '#22c55e';
  if (cur < prev) return '#ef4444';
  return '#94a3b8';
};

const TABS = [
  { id: 'overview', label: '📊 Overview' },
  { id: 'sales', label: '💼 Sales & Marketing' },
  { id: 'operations', label: '🔧 Operations' },
  { id: 'finance', label: '💰 Finance' },
  { id: 'hr', label: '👥 HR & Manpower' },
  { id: 'service', label: '🎫 Service Tickets' },
  { id: 'noc', label: '🌐 NOC & Uptime' },
];

const Card = ({ title, value, sub, color, onClick, trend, prevVal }) => (
  <div onClick={onClick} style={{
    background: '#1e293b', border: `1px solid ${color || '#334155'}`,
    borderRadius: 12, padding: '18px 20px', cursor: onClick ? 'pointer' : 'default',
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: onClick ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
  }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 6px 20px rgba(0,0,0,0.4)`; }}}
    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = onClick ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'; }}
  >
    <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: color || '#f1f5f9' }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{sub}</div>}
    {trend !== undefined && prevVal !== undefined && (
      <div style={{ fontSize: 12, color: trendColor(trend, prevVal), marginTop: 4 }}>
        {trendIcon(trend, prevVal)} vs last month: {prevVal}
      </div>
    )}
  </div>
);

const SectionHeader = ({ title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{title}</h2>
    {sub && <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>{sub}</p>}
  </div>
);

const Table = ({ cols, rows, emptyMsg }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: '#0f172a' }}>
          {cols.map(c => <th key={c} style={{ padding: '10px 12px', textAlign: 'left', color: '#94a3b8', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={cols.length} style={{ padding: 20, textAlign: 'center', color: '#475569' }}>{emptyMsg || 'No data'}</td></tr>
          : rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #1e293b', background: i % 2 === 0 ? 'transparent' : '#0f172a08' }}>
              {r.map((cell, j) => <td key={j} style={{ padding: '9px 12px', color: '#e2e8f0', whiteSpace: 'nowrap' }}>{cell}</td>)}
            </tr>
          ))
        }
      </tbody>
    </table>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    Active: '#22c55e', Online: '#22c55e', Open: '#3b82f6', Pending: '#f59e0b',
    Resolved: '#22c55e', Closed: '#64748b', 'In Progress': '#8b5cf6',
    Critical: '#ef4444', High: '#f97316', Normal: '#22c55e', Low: '#94a3b8',
    Installed: '#22c55e', Canceled: '#ef4444', Done: '#22c55e',
  };
  const bg = colors[status] || '#475569';
  return <span style={{ background: bg + '22', color: bg, border: `1px solid ${bg}44`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{status}</span>;
};

export default function GMDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState({});
  const [data, setData] = useState({});
  const [townshipStats, setTownshipStats] = useState({ by_township: {}, townships: [] });

  const fetchData = useCallback(async (key, url) => {
    setLoading(l => ({ ...l, [key]: true }));
    try {
      const r = await fetch(url, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setData(prev => ({ ...prev, [key]: d }));
      }
    } catch (e) { console.error(key, e); }
    finally { setLoading(l => ({ ...l, [key]: false })); }
  }, []);

  useEffect(() => {
    // Load township stats for GM
    fetch(`${API}/sales/stats/township-users`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setTownshipStats(d); })
      .catch(() => {});
    // Always fetch overview data
    fetchData('salesStats', `${API}/sales/stats`);
    fetchData('installStats', `${API}/install-requests/stats`);
    fetchData('complaintStats', `${API}/complaint-tickets/stats`);
    fetchData('nocStats', `${API}/noc/infra-reports`);
    fetchData('operationsStats', `${API}/operations/stats`);
    fetchData('hrAccounts', `${API}/hr/accounts`);
    fetchData('leaveSummary', `${API}/leave/summary`);
    fetchData('financeStats', `${API}/finance/ftth-new-users/stats`);
    fetchData('accountingDash', `${API}/accounting/dashboard`);
    fetchData('inventory', `${API}/store/inventory`);
  }, [fetchData]);

  useEffect(() => {
    fetchData('monthlySales', `${API}/sales/stats/monthly?month=${month}`);
    fetchData('salesEnhanced', `${API}/sales/stats/enhanced`);
    fetchData('salesMembers', `${API}/sales/members`);
    fetchData('payrollReport', `${API}/payroll/v2/report`);
    fetchData('attendanceSummary', `${API}/attendance/monthly-summary`);
    fetchData('bmftStats', `${API}/bmft/infra-down-active`);
    fetchData('radiusTownshipStats', `${API}/sales/stats/township-users`);
    fetchData('imftStats', `${API}/imft/dashboard-stats`);
    fetchData('complaints', `${API}/complaint-tickets`);
    fetchData('installRequests', `${API}/install-requests`);
    fetchData('salesRecords', `${API}/sales/records`);
    fetchData('financeNewUsers', `${API}/finance/ftth-new-users`);
  }, [fetchData, month]);

  const ss = data.salesStats || {};
  const ms = data.monthlySales || {};
  const is = data.installStats || {};
  const cs = data.complaintStats || {};
  const ops = data.operationsStats || {};
  const fs = data.financeStats || {};
  const ad = data.accountingDash || {};
  const se = data.salesEnhanced || {};
  const noc = data.nocStats || {};

  const totalActiveStaff = (data.hrAccounts || []).filter(u => u.status === 'Active').length;
  const totalStaff = (data.hrAccounts || []).length;
  const pendingLeaves = (data.leaveSummary || []).filter(l => l.status === 'Pending').length;

  // KPI calculations
  const conversionRate = ss.total ? pct(ss.installed || 0, ss.total) : '0.0';
  const avgRevPerUser = fs.total ? Math.round((ad.total_revenue || 0) / fs.total) : 0;
  const churnRate = fs.total ? pct(fs.new || 0, fs.total) : '0.0';
  const totalActiveTickets = (cs.open || 0) + (cs.in_progress || 0);
  const waitingInstall = (is.pending || 0);

  return (
    <div style={{ background: '#0f172a', minHeight: '100vh', color: '#f1f5f9', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>⭐ General Manager Dashboard</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>MyanmarLink ISP — Executive Overview</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Report Month</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '6px 12px', color: '#f1f5f9', fontSize: 13 }} />
          </div>
          <button onClick={() => navigate('/dashboard')}
            style={{ background: '#334155', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>
            ← Back
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '0 24px', display: 'flex', gap: 4, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', padding: '14px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#3b82f6' : '#64748b',
            borderBottom: tab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
            whiteSpace: 'nowrap', transition: 'color 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: 24 }}>

        {/* ============ OVERVIEW TAB ============ */}
        {tab === 'overview' && (
          <div>
            <SectionHeader title="Executive KPI Summary" sub={`Reporting period: ${month}`} />

            {/* Township Scope Notice */}
            {townshipStats.townships.length > 0 && (
              <div style={{ background: '#1e3a5f', border: '1px solid #2563eb', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#93c5fd' }}>
                <strong style={{ color: '#60a5fa' }}>📍 မြို့နယ်အလိုက် ကြည့်ရှုနေပါသည်:</strong>{' '}
                {townshipStats.townships.join(', ')}
              </div>
            )}

            {/* Township Customer Stats - Clear Active/Inactive Breakdown */}
            {townshipStats.townships.length > 0 && (() => {
              // Aggregate totals across all assigned townships
              const totals = townshipStats.townships.reduce((acc, tw) => {
                const d = townshipStats.by_township[tw] || {};
                acc.total += d.total || 0;
                acc.active += d.active || 0;
                acc.inactive += d.expired || 0;
                acc.pending += d.pending_installs || 0;
                acc.complaints += d.complaint_tickets || 0;
                return acc;
              }, { total: 0, active: 0, inactive: 0, pending: 0, complaints: 0 });

              return (
                <>
                  {/* Summary KPI Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>စုစုပေါင်း Customer</div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: '#f1f5f9' }}>{totals.total}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>မြို့နယ် {townshipStats.townships.length} ခု</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #22c55e55', borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#86efac', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>✅ Active</div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: '#22c55e' }}>{totals.active}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>တပ်ဆင်ပြီး သုံးနေသော</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #ef444455', borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#fca5a5', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>⏰ Expired</div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: '#ef4444' }}>{totals.inactive}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>ဖျက်သိမ်းပြီး / မသုံးတော့</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #f59e0b55', borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#fcd34d', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>🏗️ Pending Install</div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: '#f59e0b' }}>{totals.pending}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>တပ်ဆင်ဆဲ</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #f8717155', borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#fca5a5', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>🎫 Complaints</div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: '#f87171' }}>{totals.complaints}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>ဖြေရှင်းဆဲ</div>
                    </div>
                  </div>

                  {/* Per-Township Breakdown Table */}
                  <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 20, marginBottom: 24 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 14 }}>🗺️ မြို့နယ်အလိုက် Customer အသေးစိတ်</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#0f172a' }}>
                            <th style={{ padding: '10px 12px', textAlign: 'left', color: '#94a3b8', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>မြို့နယ်</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', color: '#f1f5f9', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>စုစုပေါင်း</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', color: '#22c55e', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>✅ Active</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>⏰ Expired</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', color: '#f59e0b', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>🏗️ Pending Install</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', color: '#f87171', borderBottom: '1px solid #334155', whiteSpace: 'nowrap' }}>🎫 Complaints</th>
                          </tr>
                        </thead>
                        <tbody>
                          {townshipStats.townships.map((tw, i) => {
                            const d = townshipStats.by_township[tw] || {};
                            const total = d.total || 0;
                            const active = d.active || 0;
                            const inactive = d.expired || 0;
                            const pending = d.pending_installs || 0;
                            const complaints = d.complaint_tickets || 0;
                            return (
                              <tr key={tw} style={{ borderBottom: '1px solid #334155', background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}>
                                <td style={{ padding: '10px 12px', color: '#e2e8f0', fontWeight: 600 }}>{tw}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#f1f5f9', fontWeight: 700 }}>{total}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                  <span style={{ background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e44', borderRadius: 6, padding: '2px 10px', fontWeight: 700 }}>{active}</span>
                                </td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                  <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444', borderRadius: 6, padding: '2px 10px', fontWeight: 700 }}>{inactive}</span>
                                </td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                  <span style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: 6, padding: '2px 10px', fontWeight: 700 }}>{pending}</span>
                                </td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                  <span style={{ background: '#f8717122', color: '#f87171', border: '1px solid #f8717144', borderRadius: 6, padding: '2px 10px', fontWeight: 700 }}>{complaints}</span>
                                </td>
                              </tr>
                            );
                          })}
                          {/* Totals row */}
                          <tr style={{ borderTop: '2px solid #475569', background: '#0f172a' }}>
                            <td style={{ padding: '10px 12px', color: '#94a3b8', fontWeight: 700 }}>စုစုပေါင်း</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#f1f5f9', fontWeight: 800 }}>{totals.total}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#22c55e', fontWeight: 800 }}>{totals.active}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontWeight: 800 }}>{totals.inactive}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#f59e0b', fontWeight: 800 }}>{totals.pending}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#f87171', fontWeight: 800 }}>{totals.complaints}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Top KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Sales This Month" value={fmt(ms.total_count)} sub={`Revenue: ${fmtMMK(ms.total_revenue)}`} color="#3b82f6"
                trend={ms.total_count} prevVal={ms.prev_month_count} onClick={() => setTab('sales')} />
              <Card title="Install Finished (Month)" value={fmt(is.completed)} sub={`Pending: ${fmt(is.pending)}`} color="#22c55e"
                onClick={() => setTab('operations')} />
              <Card title="Active Service Tickets" value={fmt(totalActiveTickets)} sub={`Open: ${fmt(cs.open)} | In Progress: ${fmt(cs.in_progress)}`} color="#f59e0b"
                onClick={() => setTab('service')} />
              <Card title="Monthly Revenue" value={fmtMMK(ms.total_revenue)} sub={`Prev month: ${fmtMMK(ms.prev_month_revenue)}`} color="#8b5cf6"
                trend={ms.total_revenue} prevVal={ms.prev_month_revenue} onClick={() => setTab('finance')} />
              <Card title="Sale Conversion Rate" value={`${conversionRate}%`} sub={`${fmt(ss.installed)} installed / ${fmt(ss.total)} total`} color="#06b6d4"
                onClick={() => setTab('sales')} />
              <Card title="Active Staff" value={`${totalActiveStaff} / ${totalStaff}`} sub={`Pending Leaves: ${pendingLeaves}`} color="#10b981"
                onClick={() => setTab('hr')} />
              <Card title="New Customers (Month)" value={fmt(fs.this_month)} sub={`Total Active: ${fmt(fs.total)}`} color="#f97316"
                onClick={() => setTab('finance')} />
              <Card title="Waiting for Install" value={fmt(waitingInstall)} sub="New customers pending installation" color="#ef4444"
                onClick={() => setTab('operations')} />
            </div>

            {/* Row 2 — Service type breakdown */}
            <SectionHeader title="Monthly Sales by Service Type" sub="FTTH / DIA / DPLC / IPT breakdown" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="FTTH Sales" value={fmt(ms.FTTH)} sub={`Revenue: ${fmtMMK(ms.ftth_revenue)}`} color="#3b82f6" onClick={() => setTab('sales')} />
              <Card title="DIA Sales" value={fmt(ms.DIA)} sub={`Revenue: ${fmtMMK(ms.dia_revenue)}`} color="#8b5cf6" onClick={() => setTab('sales')} />
              <Card title="DPLC Sales" value={fmt(ms.DPLC)} sub={`Revenue: ${fmtMMK(ms.dplc_revenue)}`} color="#06b6d4" onClick={() => setTab('sales')} />
              <Card title="IPT Sales" value={fmt(ms.IPT)} sub={`Revenue: ${fmtMMK(ms.ipt_revenue)}`} color="#10b981" onClick={() => setTab('sales')} />
              <Card title="25 Mbps Packages" value={fmt(ms['25mbps'])} sub="Bandwidth tier" color="#f59e0b" />
              <Card title="35 Mbps Packages" value={fmt(ms['35mbps'])} sub="Bandwidth tier" color="#f97316" />
              <Card title="55 Mbps Packages" value={fmt(ms['55mbps'])} sub="Bandwidth tier" color="#ef4444" />
            </div>

            {/* RadiusManager Township User Status Section */}
            {(() => {
              const rts = data.radiusTownshipStats || {};
              const byTownship = rts.by_township || {};
              const townshipNames = rts.townships || [];
              if (townshipNames.length === 0) return null;
              // Build summary totals
              let sumTotal = 0, sumActive = 0, sumExpired = 0, sumEnabled = 0, sumDisabled = 0;
              townshipNames.forEach(tw => {
                const t = byTownship[tw] || {};
                sumTotal += t.total || 0;
                sumActive += t.active || 0;
                sumExpired += t.expired || 0;
                sumEnabled += t.enabled || 0;
                sumDisabled += t.disabled || 0;
              });
              return (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ marginBottom: 16 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>🗺️ မြို့နယ်အလိုက် Customer အသေးစိတ်</h2>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>RadiusManager မှ Real-time data — မြို့နယ် {townshipNames.length} ခု</p>
                  </div>
                  {/* Summary KPI Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 18px' }}>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>👥 စုစုပေါင်း Customer</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#f1f5f9' }}>{fmt(sumTotal)}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>မြို့နယ် {townshipNames.length} ခုမှ</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #22c55e44', borderRadius: 12, padding: '16px 18px' }}>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>✅ Active</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#22c55e' }}>{fmt(sumActive)}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>တက်ကြွစွာ သုံးနေသော</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #f59e0b44', borderRadius: 12, padding: '16px 18px' }}>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>⏰ Expired</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#f59e0b' }}>{fmt(sumExpired)}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>သက်တမ်းကုန်သော</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #3b82f644', borderRadius: 12, padding: '16px 18px' }}>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>🔵 Enabled</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#3b82f6' }}>{fmt(sumEnabled)}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>ဖွင့်ထားသော</div>
                    </div>
                    <div style={{ background: '#1e293b', border: '1px solid #ef444444', borderRadius: 12, padding: '16px 18px' }}>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>🔴 Disabled</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#ef4444' }}>{fmt(sumDisabled)}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>ပိတ်ထားသော</div>
                    </div>
                  </div>
                  {/* Per-township breakdown table */}
                  <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, overflowX: 'auto' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>မြို့နယ်တစ်ခုချင်းစီ Customer Status (RadiusManager)</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#0f172a' }}>
                          {['မြို့နယ်', 'စုစုပေါင်း', '✅ Active', '⏰ Expired', '🔵 Enabled', '🔴 Disabled', '🏗️ Pending Install', '🎫 Open Tickets'].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: h === 'မြို့နယ်' ? 'left' : 'center', color: '#94a3b8', borderBottom: '1px solid #334155', whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {townshipNames.map((tw, i) => {
                          const t = byTownship[tw] || {};
                          return (
                            <tr key={tw} style={{ borderBottom: '1px solid #1e293b', background: i % 2 === 0 ? 'transparent' : '#0f172a20' }}>
                              <td style={{ padding: '10px 14px', color: '#e2e8f0', fontWeight: 600 }}>{tw}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f1f5f9', fontWeight: 700 }}>{fmt(t.total || 0)}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                <span style={{ background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e44', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{fmt(t.active || 0)}</span>
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                <span style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{fmt(t.expired || 0)}</span>
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                <span style={{ background: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f644', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{fmt(t.enabled || 0)}</span>
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{fmt(t.disabled || 0)}</span>
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f59e0b' }}>{fmt(t.pending_installs || 0)}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#ef4444' }}>{fmt(t.complaint_tickets || 0)}</td>
                            </tr>
                          );
                        })}
                        {/* Total row */}
                        <tr style={{ borderTop: '2px solid #334155', background: '#0f172a' }}>
                          <td style={{ padding: '10px 14px', color: '#f1f5f9', fontWeight: 700 }}>🔢 စုစုပေါင်း</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f1f5f9', fontWeight: 700 }}>{fmt(sumTotal)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#22c55e', fontWeight: 700 }}>{fmt(sumActive)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f59e0b', fontWeight: 700 }}>{fmt(sumExpired)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#3b82f6', fontWeight: 700 }}>{fmt(sumEnabled)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#ef4444', fontWeight: 700 }}>{fmt(sumDisabled)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#f59e0b', fontWeight: 700 }}>{fmt(townshipNames.reduce((s, tw) => s + ((byTownship[tw] || {}).pending_installs || 0), 0))}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: '#ef4444', fontWeight: 700 }}>{fmt(townshipNames.reduce((s, tw) => s + ((byTownship[tw] || {}).complaint_tickets || 0), 0))}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
            {/* NOC quick stats */}
            <SectionHeader title="NOC & Network Health" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Infrastructure Reports" value={fmt((noc.reports || []).length)} sub="Total infra issues reported" color="#ef4444" onClick={() => setTab('noc')} />
              <Card title="BMFT Active Downtime" value={fmt((data.bmftStats || []).length)} sub="Active infrastructure down" color="#f97316" onClick={() => setTab('noc')} />
              <Card title="Avg Time to Repair (MTTR)" value={ops.avg_resolution_hours ? `${ops.avg_resolution_hours}h` : 'N/A'} sub="Mean time to repair" color="#8b5cf6" onClick={() => setTab('noc')} />
            </div>
          </div>
        )}

        {/* ============ SALES & MARKETING TAB ============ */}
        {tab === 'sales' && (
          <div>
            <SectionHeader title="Sales & Marketing Reports" sub={`Month: ${month}`} />

            {/* Monthly totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Sales" value={fmt(ms.total_count)} sub={`Prev: ${fmt(ms.prev_month_count)}`} color="#3b82f6" trend={ms.total_count} prevVal={ms.prev_month_count} />
              <Card title="Total Revenue" value={fmtMMK(ms.total_revenue)} sub={`Prev: ${fmtMMK(ms.prev_month_revenue)}`} color="#22c55e" />
              <Card title="Approved/Installed" value={fmt(ms.approved)} sub={`Pending: ${fmt(ms.pending)}`} color="#10b981" />
              <Card title="Cancelled" value={fmt(ms.cancelled)} sub="This month" color="#ef4444" />
              <Card title="Conversion Rate" value={`${conversionRate}%`} color="#8b5cf6" />
            </div>

            {/* Service type breakdown */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Service Type Breakdown — {month}</h3>
              <Table
                cols={['Service', 'Count', 'Revenue (MMK)', 'Share %']}
                rows={[
                  ['FTTH', fmt(ms.FTTH), fmtMMK(ms.ftth_revenue), `${pct(ms.FTTH, ms.total_count)}%`],
                  ['DIA', fmt(ms.DIA), fmtMMK(ms.dia_revenue), `${pct(ms.DIA, ms.total_count)}%`],
                  ['DPLC', fmt(ms.DPLC), fmtMMK(ms.dplc_revenue), `${pct(ms.DPLC, ms.total_count)}%`],
                  ['IPT', fmt(ms.IPT), fmtMMK(ms.ipt_revenue), `${pct(ms.IPT, ms.total_count)}%`],
                  ['B2B', fmt(ms.B2B), '—', `${pct(ms.B2B, ms.total_count)}%`],
                ]}
              />
            </div>

            {/* Bandwidth breakdown */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Bandwidth Package Distribution</h3>
              <Table
                cols={['Package', 'Count', 'Share %']}
                rows={[
                  ['25 Mbps', fmt(ms['25mbps']), `${pct(ms['25mbps'], ms.total_count)}%`],
                  ['35 Mbps', fmt(ms['35mbps']), `${pct(ms['35mbps'], ms.total_count)}%`],
                  ['55 Mbps', fmt(ms['55mbps']), `${pct(ms['55mbps'], ms.total_count)}%`],
                ]}
              />
            </div>

            {/* Individual performance */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Individual Sales Performance</h3>
              <Table
                cols={['Sales Member', 'Total', 'Done', 'Pending', 'Canceled', 'Conversion %']}
                rows={Object.entries(se.member_stats || {}).map(([name, s]) => [
                  name, fmt(s.total), fmt(s.done), fmt(s.pending), fmt(s.canceled),
                  `${pct(s.done, s.total)}%`
                ])}
                emptyMsg="No sales data"
              />
            </div>

            {/* Recent sales records */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Recent Sales Records</h3>
              <Table
                cols={['Date', 'Customer', 'Service', 'Bandwidth', 'Price', 'Status', 'Sales Member']}
                rows={(data.salesRecords || []).slice(0, 20).map(s => [
                  s.created_at ? s.created_at.slice(0, 10) : '—',
                  s.customer_name || '—',
                  s.service_type || s.team_type || '—',
                  s.bandwidth || '—',
                  fmtMMK(s.total_price || s.price),
                  <StatusBadge status={s.status} />,
                  s.sales_member || s.sales_name || '—',
                ])}
                emptyMsg="No sales records"
              />
            </div>
          </div>
        )}

        {/* ============ OPERATIONS TAB ============ */}
        {tab === 'operations' && (
          <div>
            <SectionHeader title="Operations Reports" sub="IMFT, BMFT, Install Requests" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Install Requests" value={fmt(is.total)} sub="All time" color="#3b82f6" />
              <Card title="Completed Installs" value={fmt(is.completed)} sub="Successfully installed" color="#22c55e" />
              <Card title="Pending Installs" value={fmt(is.pending)} sub="Waiting for installation" color="#f59e0b" />
              <Card title="In Progress" value={fmt(is.in_progress)} sub="Currently being installed" color="#8b5cf6" />
              <Card title="IMFT Tasks" value={fmt((data.imftStats || {}).total_tasks)} sub={`Pending: ${fmt((data.imftStats || {}).pending_tasks)}`} color="#06b6d4" />
              <Card title="BMFT Active Downtime" value={fmt((data.bmftStats || []).length)} sub="Active infrastructure issues" color="#ef4444" />
            </div>

            {/* Install requests detail */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>New Customers Waiting for Install</h3>
              <Table
                cols={['Date', 'Customer', 'Box Name', 'Township', 'Service', 'Status', 'Assigned To']}
                rows={(data.installRequests || []).filter(r => r.status === 'Pending').slice(0, 20).map(r => [
                  r.created_at ? r.created_at.slice(0, 10) : '—',
                  r.customer_name || '—',
                  r.box_name || '—',
                  r.township || '—',
                  r.service_type || r.team_type || '—',
                  <StatusBadge status={r.status} />,
                  r.assigned_to || '—',
                ])}
                emptyMsg="No pending installs"
              />
            </div>

            {/* Installation details with GPS */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Installation Details (with GPS)</h3>
              <Table
                cols={['Customer', 'Box Name', 'Customer GPS', 'Tower GPS', 'Status', 'Date']}
                rows={(data.installRequests || []).slice(0, 20).map(r => [
                  r.customer_name || '—',
                  r.box_name || '—',
                  r.customer_lat && r.customer_lng ? `${r.customer_lat}, ${r.customer_lng}` : '—',
                  r.tower_lat && r.tower_lng ? `${r.tower_lat}, ${r.tower_lng}` : '—',
                  <StatusBadge status={r.status} />,
                  r.created_at ? r.created_at.slice(0, 10) : '—',
                ])}
                emptyMsg="No installation data"
              />
            </div>

            {/* BMFT infra down */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Active Infrastructure Downtime</h3>
              <Table
                cols={['Report ID', 'Location', 'Issue', 'Reported By', 'Started', 'Duration']}
                rows={(data.bmftStats || []).slice(0, 20).map(r => {
                  const started = r.started_at ? new Date(r.started_at) : null;
                  const duration = started ? `${Math.round((Date.now() - started.getTime()) / 3600000)}h` : '—';
                  return [
                    r.report_id || r._id || '—',
                    r.location || '—',
                    r.issue_type || r.description || '—',
                    r.reported_by || '—',
                    started ? started.toLocaleDateString() : '—',
                    duration,
                  ];
                })}
                emptyMsg="No active downtime"
              />
            </div>
          </div>
        )}

        {/* ============ FINANCE TAB ============ */}
        {tab === 'finance' && (
          <div>
            <SectionHeader title="Finance Reports" sub="Revenue, billing, and customer financials" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Revenue (Month)" value={fmtMMK(ms.total_revenue)} sub={`Prev: ${fmtMMK(ms.prev_month_revenue)}`} color="#22c55e" trend={ms.total_revenue} prevVal={ms.prev_month_revenue} />
              <Card title="Total Active Customers" value={fmt(fs.total)} sub="Billing-tracked customers" color="#3b82f6" />
              <Card title="New Customers (Month)" value={fmt(fs.this_month)} sub="Activated this month" color="#10b981" />
              <Card title="Billed Customers" value={fmt(fs.billed)} sub="Successfully billed" color="#8b5cf6" />
              <Card title="Avg Revenue / User" value={fmtMMK(avgRevPerUser)} sub="ARPU this month" color="#f59e0b" />
              <Card title="Churn Rate" value={`${churnRate}%`} sub="New vs total ratio" color="#ef4444" />
              <Card title="Total Accounting Revenue" value={fmtMMK(ad.total_revenue)} sub="All-time accounting" color="#06b6d4" />
              <Card title="Total Expenses" value={fmtMMK(ad.total_expenses)} sub="All-time accounting" color="#f97316" />
            </div>

            {/* Finance new users table */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>New Customer Billing List</h3>
              <Table
                cols={['Date', 'Customer', 'Username', 'Package', 'Amount', 'Billed', 'Status']}
                rows={(data.financeNewUsers || []).slice(0, 20).map(u => [
                  u.activated_at ? u.activated_at.slice(0, 10) : '—',
                  u.customer_name || '—',
                  u.username || '—',
                  u.package || '—',
                  fmtMMK(u.amount),
                  u.billed ? '✅ Yes' : '❌ No',
                  <StatusBadge status={u.status || 'New'} />,
                ])}
                emptyMsg="No billing data"
              />
            </div>
          </div>
        )}

        {/* ============ HR TAB ============ */}
        {tab === 'hr' && (
          <div>
            <SectionHeader title="HR & Manpower Reports" sub="Staff, attendance, payroll, and turnover" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Staff" value={fmt(totalStaff)} sub="All departments" color="#3b82f6" />
              <Card title="Active Staff" value={fmt(totalActiveStaff)} sub="Currently active" color="#22c55e" />
              <Card title="Inactive Staff" value={fmt(totalStaff - totalActiveStaff)} sub="Inactive/suspended" color="#ef4444" />
              <Card title="Pending Leave Requests" value={fmt(pendingLeaves)} sub="Awaiting approval" color="#f59e0b" />
            </div>

            {/* Staff by department */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Staff by Department</h3>
              {(() => {
                const deptMap = {};
                (data.hrAccounts || []).forEach(u => {
                  const dept = u.department || 'Unknown';
                  if (!deptMap[dept]) deptMap[dept] = { total: 0, active: 0 };
                  deptMap[dept].total++;
                  if (u.status === 'Active') deptMap[dept].active++;
                });
                return (
                  <Table
                    cols={['Department', 'Total', 'Active', 'Inactive']}
                    rows={Object.entries(deptMap).sort((a, b) => b[1].total - a[1].total).map(([dept, s]) => [
                      dept, fmt(s.total), fmt(s.active), fmt(s.total - s.active)
                    ])}
                    emptyMsg="No staff data"
                  />
                );
              })()}
            </div>

            {/* Leave summary */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Leave Requests Summary</h3>
              <Table
                cols={['Staff Name', 'Department', 'Leave Type', 'From', 'To', 'Days', 'Status']}
                rows={(data.leaveSummary || []).slice(0, 20).map(l => [
                  l.staff_name || l.name || '—',
                  l.department || '—',
                  l.leave_type || '—',
                  l.start_date ? l.start_date.slice(0, 10) : '—',
                  l.end_date ? l.end_date.slice(0, 10) : '—',
                  l.days || '—',
                  <StatusBadge status={l.status || 'Pending'} />,
                ])}
                emptyMsg="No leave requests"
              />
            </div>

            {/* Attendance summary */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Monthly Attendance Summary</h3>
              <Table
                cols={['Staff Name', 'Department', 'Present Days', 'Absent Days', 'Late Days', 'Attendance %']}
                rows={(data.attendanceSummary || []).slice(0, 20).map(a => [
                  a.staff_name || a.name || '—',
                  a.department || '—',
                  fmt(a.present_days),
                  fmt(a.absent_days),
                  fmt(a.late_days),
                  `${pct(a.present_days, (a.present_days || 0) + (a.absent_days || 0))}%`,
                ])}
                emptyMsg="No attendance data"
              />
            </div>

            {/* Payroll report */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Payroll Report</h3>
              <Table
                cols={['Staff Name', 'Department', 'Basic Salary', 'Allowances', 'Deductions', 'Net Pay', 'Status']}
                rows={(data.payrollReport || []).slice(0, 20).map(p => [
                  p.staff_name || p.name || '—',
                  p.department || '—',
                  fmtMMK(p.basic_salary),
                  fmtMMK(p.allowances),
                  fmtMMK(p.deductions),
                  fmtMMK(p.net_pay),
                  <StatusBadge status={p.status || 'Pending'} />,
                ])}
                emptyMsg="No payroll data"
              />
            </div>
          </div>
        )}

        {/* ============ SERVICE TICKETS TAB ============ */}
        {tab === 'service' && (
          <div>
            <SectionHeader title="Service Complaint Tickets" sub="All departments — active and resolved tickets" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Tickets" value={fmt(cs.total)} color="#3b82f6" />
              <Card title="Open" value={fmt(cs.open)} color="#f59e0b" />
              <Card title="In Progress" value={fmt(cs.in_progress)} color="#8b5cf6" />
              <Card title="Resolved" value={fmt(cs.resolved)} color="#22c55e" />
              <Card title="Closed" value={fmt(cs.closed)} color="#64748b" />
              <Card title="Active Tickets" value={fmt(totalActiveTickets)} sub="Open + In Progress" color="#ef4444" />
            </div>

            {/* Ticket list */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>All Service Complaint Tickets</h3>
              <Table
                cols={['PPPoE ID', 'Customer', 'Type', 'Priority', 'Status', 'Assigned To', 'Created By', 'Date']}
                rows={(data.complaints || []).slice(0, 30).map(t => [
                  t.customer_id ? React.createElement('span', {style:{background:'#E3F2FD',color:'#1565C0',padding:'2px 8px',borderRadius:4,fontFamily:'monospace',fontSize:11,fontWeight:700,border:'1px solid #90CAF9'}}, t.customer_id) : (t.ticket_id || '—'),
                  t.customer_name || '—',
                  t.complaint_type || '—',
                  <StatusBadge status={t.priority || 'Normal'} />,
                  <StatusBadge status={t.status || 'Open'} />,
                  t.assigned_to || '—',
                  t.created_by_name || '—',
                  t.created_at ? t.created_at.slice(0, 10) : '—',
                ])}
                emptyMsg="No tickets found"
              />
            </div>

            {/* MTTR & FCR metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Mean Time to Repair (MTTR)" value={ops.avg_resolution_hours ? `${ops.avg_resolution_hours}h` : 'N/A'} sub="Average resolution time" color="#8b5cf6" />
              <Card title="First Contact Resolution" value={ops.fcr_rate ? `${ops.fcr_rate}%` : 'N/A'} sub="Resolved on first contact" color="#22c55e" />
              <Card title="Net Promoter Score" value={ops.nps_score !== undefined ? ops.nps_score : 'N/A'} sub="Customer satisfaction" color="#3b82f6" />
            </div>
          </div>
        )}

        {/* ============ NOC TAB ============ */}
        {tab === 'noc' && (
          <div>
            <SectionHeader title="NOC & Network Uptime Reports" sub="Infrastructure, uptime, downtime, and capacity" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="Total Infra Reports" value={fmt((noc.reports || []).length)} color="#3b82f6" />
              <Card title="Active Downtime Events" value={fmt((data.bmftStats || []).length)} color="#ef4444" />
              <Card title="Total Capacity" value={ops.total_capacity ? `${fmt(ops.total_capacity)} ports` : 'N/A'} sub="Network capacity" color="#8b5cf6" />
              <Card title="Connected Users" value={fmt(ops.connected_users)} sub="Currently connected" color="#22c55e" />
              <Card title="Capacity Utilization" value={ops.total_capacity ? `${pct(ops.connected_users, ops.total_capacity)}%` : 'N/A'} color="#f59e0b" />
            </div>

            {/* NOC infra reports */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Infrastructure Reports (NOC)</h3>
              <Table
                cols={['Report ID', 'Type', 'Location', 'Severity', 'Status', 'Reported By', 'Date']}
                rows={(noc.reports || []).slice(0, 20).map(r => [
                  r.report_id || '—',
                  r.issue_type || r.type || '—',
                  r.location || '—',
                  <StatusBadge status={r.severity || 'Normal'} />,
                  <StatusBadge status={r.status || 'Open'} />,
                  r.reported_by || '—',
                  r.created_at ? r.created_at.slice(0, 10) : '—',
                ])}
                emptyMsg="No infra reports"
              />
            </div>

            {/* Uptime/Downtime summary */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#f1f5f9' }}>Active Infrastructure Downtime (BMFT)</h3>
              <Table
                cols={['Report ID', 'Location', 'Issue', 'Reported By', 'Started', 'Duration']}
                rows={(data.bmftStats || []).slice(0, 20).map(r => {
                  const started = r.started_at ? new Date(r.started_at) : null;
                  const duration = started ? `${Math.round((Date.now() - started.getTime()) / 3600000)}h` : '—';
                  return [
                    r.report_id || '—',
                    r.location || '—',
                    r.issue_type || r.description || '—',
                    r.reported_by || '—',
                    started ? started.toLocaleDateString() : '—',
                    duration,
                  ];
                })}
                emptyMsg="No active downtime"
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
